import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { createSettlement, getSettlementConfig } from 'api/transactions';
import { showError, showToast } from 'utils/notifications';

const CUSTOM_REFUND_SETTLEMENT = 'custom_refund_settlement';
const OWN_ACCOUNT_TRANSFER = 'own_account_transfer';

export default function CreateSettlement() {
  const [settlementConfig, setSettlementConfig] = useState<any>({});
  const [selectedSupermerchantId, setSelectedSupermerchantId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSettlementType, setSelectedSettlementType] = useState('');
  const [sourceCurrency, setSourceCurrency] = useState('');
  const [destinationCurrency, setDestinationCurrency] = useState('');
  const [sourceAmount, setSourceAmount] = useState('');
  /** Value sent as `source_account_number`; must match backend mapping keys (e.g. CMA IBAN from config). */
  const [selectedSourceAccountNumber, setSelectedSourceAccountNumber] = useState('');
  const [beneficiaryAccountNumber, setBeneficiaryAccountNumber] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [unmappedCreditId, setUnmappedCreditId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();
  const history = useHistory();

  const isCustomRefund =
    selectedSettlementType === CUSTOM_REFUND_SETTLEMENT || selectedSettlementType === OWN_ACCOUNT_TRANSFER;

  useEffect(() => {
    fetchSettlementConfig();
  }, []);

  const fetchSettlementConfig = async () => {
    try {
      setIsLoading(true);
      const response = await getSettlementConfig();

      setSettlementConfig(response?.data || {});
    } catch (error) {
      showError(toast, 'Error fetching settlement config', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetCustomRefundFields = () => {
    setSelectedSourceAccountNumber('');
    setBeneficiaryAccountNumber('');
    setBeneficiaryName('');
    setUnmappedCreditId('');
  };

  const handleSupermerchantChange = (supermerchantId: string) => {
    setSelectedSupermerchantId(supermerchantId);
    setSelectedCategory('');
    setSelectedSettlementType('');
    setSourceCurrency('');
    setDestinationCurrency('');
    resetCustomRefundFields();
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setSelectedSettlementType('');
    setSourceCurrency('');
    setDestinationCurrency('');
    resetCustomRefundFields();
  };

  const handleSettlementTypeChange = (settlementType: string) => {
    setSelectedSettlementType(settlementType);
    resetCustomRefundFields();

    if (selectedSupermerchantId && selectedCategory && settlementType) {
      const config = settlementConfig[selectedSupermerchantId]?.[selectedCategory]?.[settlementType];

      if (config) {
        setSourceCurrency(config.source_currency);
        setDestinationCurrency(config.destination_currency);
      }
    }
  };

  const getSourceAccounts = (): any[] => {
    if (!isCustomRefund || !selectedSupermerchantId || !selectedCategory) return [];
    const config = settlementConfig[selectedSupermerchantId]?.[selectedCategory]?.[CUSTOM_REFUND_SETTLEMENT];

    return config?.source_account || [];
  };

  const getSourceAccountOptionValue = (account: { account_number?: string; account_id?: string }) =>
    account.account_number || account.account_id || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !selectedSupermerchantId ||
      !selectedCategory ||
      !selectedSettlementType ||
      !sourceAmount ||
      !sourceCurrency ||
      !destinationCurrency
    ) {
      showToast(toast, 'Error', 'Please fill in all required fields', 'error');

      return;
    }

    if (
      isCustomRefund &&
      (!selectedSourceAccountNumber || !beneficiaryName.trim() || !beneficiaryAccountNumber.trim())
    ) {
      showToast(
        toast,
        'Error',
        'Please fill in source account, beneficiary name, and beneficiary account number',
        'error'
      );

      return;
    }

    try {
      setIsSubmitting(true);
      const payload: any = {
        source_amount: parseFloat(sourceAmount),
        source_currency: sourceCurrency,
        destination_currency: destinationCurrency,
        settlement_type: selectedSettlementType,
      };

      if (isCustomRefund) {
        payload.source_account_number = selectedSourceAccountNumber;
        payload.beneficiary_name = beneficiaryName.trim();
        payload.beneficiary_account_number = beneficiaryAccountNumber.trim();
        if (unmappedCreditId.trim()) {
          payload.unmapped_credit_id = unmappedCreditId.trim();
        }
      }

      await createSettlement(selectedSupermerchantId, payload);
      showToast(toast, 'Success', 'Settlement created successfully.', 'success');
      history.goBack();
    } catch (error) {
      showError(toast, 'Error creating settlement', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const supermerchantIds = Object.keys(settlementConfig);
  const categories = selectedSupermerchantId ? Object.keys(settlementConfig[selectedSupermerchantId] || {}) : [];
  const settlementTypes =
    selectedSupermerchantId && selectedCategory
      ? Object.keys(settlementConfig[selectedSupermerchantId]?.[selectedCategory] || {})
      : [];
  const sourceAccounts = getSourceAccounts();

  const isSubmitDisabled =
    !selectedSupermerchantId ||
    !selectedCategory ||
    !selectedSettlementType ||
    !sourceAmount ||
    isLoading ||
    (isCustomRefund && (!selectedSourceAccountNumber || !beneficiaryName.trim() || !beneficiaryAccountNumber.trim()));

  return (
    <Container maxW='100%' py={8}>
      <HStack mb={6} spacing={10} align='center'>
        <Button colorScheme='gray' onClick={() => history.goBack()} size='md'>
          Back
        </Button>
        <Heading as='h1' size='lg' flex='1' textAlign='center'>
          Create Settlement
        </Heading>
      </HStack>

      <Box maxW='600px' mx='auto'>
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align='stretch'>
            <FormControl isRequired>
              <FormLabel>Supermerchant ID</FormLabel>
              <Select
                placeholder='Select supermerchant'
                value={selectedSupermerchantId}
                onChange={(e) => handleSupermerchantChange(e.target.value)}
                isDisabled={isLoading}
              >
                {supermerchantIds.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Category</FormLabel>
              <Select
                placeholder='Select category'
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                isDisabled={!selectedSupermerchantId || isLoading}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Settlement Type</FormLabel>
              <Select
                placeholder='Select settlement type'
                value={selectedSettlementType}
                onChange={(e) => handleSettlementTypeChange(e.target.value)}
                isDisabled={!selectedCategory || isLoading}
              >
                {settlementTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Source Currency</FormLabel>
              <Input
                value={sourceCurrency}
                isReadOnly
                bg='gray.100'
                placeholder='Will be auto-filled based on settlement type'
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Destination Currency</FormLabel>
              <Input
                value={destinationCurrency}
                isReadOnly
                bg='gray.100'
                placeholder='Will be auto-filled based on settlement type'
              />
            </FormControl>

            {isCustomRefund && (
              <>
                <FormControl isRequired>
                  <FormLabel>Source account number</FormLabel>
                  <Select
                    placeholder='Select source account'
                    value={selectedSourceAccountNumber}
                    onChange={(e) => setSelectedSourceAccountNumber(e.target.value)}
                  >
                    {sourceAccounts.map((account: any) => {
                      const value = getSourceAccountOptionValue(account);

                      return (
                        <option key={value} value={value}>
                          {account.account_number ?? account.account_id}
                          {account.account_id && account.account_number ? ` (${account.account_id})` : ''}
                        </option>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Beneficiary name</FormLabel>
                  <Input
                    value={beneficiaryName}
                    onChange={(e) => setBeneficiaryName(e.target.value)}
                    placeholder='Beneficiary name'
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Beneficiary account number</FormLabel>
                  <Input
                    value={beneficiaryAccountNumber}
                    onChange={(e) => setBeneficiaryAccountNumber(e.target.value)}
                    placeholder='IBAN or beneficiary bank account number'
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Unmapped Credit ID</FormLabel>
                  <Input
                    value={unmappedCreditId}
                    onChange={(e) => setUnmappedCreditId(e.target.value)}
                    placeholder='Partner transaction request ID of the unmapped credit'
                  />
                </FormControl>
              </>
            )}

            <HStack spacing={4} align='end'>
              <FormControl isRequired flex='1'>
                <FormLabel>Source Amount</FormLabel>
                <Input
                  type='number'
                  step='0.01'
                  value={sourceAmount}
                  onChange={(e) => setSourceAmount(e.target.value)}
                  placeholder='Enter amount'
                />
              </FormControl>
              <Box minW='100px' pt={8}>
                <Input value={sourceCurrency} isReadOnly bg='gray.100' textAlign='center' />
              </Box>
            </HStack>

            <Button type='submit' colorScheme='blue' size='lg' isLoading={isSubmitting} isDisabled={isSubmitDisabled}>
              Create Settlement
            </Button>
          </VStack>
        </form>
      </Box>
    </Container>
  );
}
